import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { sitewideApi, settingsApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { StatusBadge, PriorityBadge, TeamBadge, OverdueBadge } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Input, { Select, Textarea } from '../components/ui/Input';
import { FormattedNotes, NotesEditor } from '../components/ui/MarkdownNotes';
import { FollowUpOwnerSelect, MultiAssigneePicker } from '../components/ui/AssignmentFields';
import { TableSkeleton } from '../components/ui/Skeleton';
import {
  formatAssigneeNames,
  formatDate,
  getOverdueDays,
  getTeamsForAssignees,
  memberCanSeeTask,
  parseAssigneeList,
  PRIORITIES,
  STATUSES,
  TEAMS,
} from '../lib/utils';

const taskSchema = z.object({
  website_id: z.coerce.number().int().positive('Website is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  assigned_to: z.string().optional(),
  assigned_to_list: z.array(z.string()).optional(),
  assigned_team: z.string().optional(),
  follow_up_by: z.string().optional().nullable(),
  priority: z.enum(['Low', 'Medium', 'High']),
  status: z.enum(['Pending', 'In Progress', 'Done', 'Blocked']),
  due_date: z.string().min(1, 'Due date is required'),
  notes: z.string().optional(),
  estimated_hours: z.coerce.number().int().min(0).max(999).default(0),
  estimated_minutes: z.coerce.number().int().min(0).max(59).default(0),
}).refine(data => (data.estimated_hours * 60 + data.estimated_minutes) > 0, {
  message: 'Time estimate is required',
  path: ['estimated_minutes'],
});
type TaskFormData = z.infer<typeof taskSchema>;

const HOURS = Array.from({ length: 100 }, (_, i) => ({ value: String(i), label: `${i}h` }));
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => ({ value: String(m), label: `${m}m` }));

function TaskForm({ defaultValues, onSubmit, loading, submitLabel }: {
  defaultValues?: Partial<TaskFormData>;
  onSubmit: (data: TaskFormData) => void;
  loading: boolean;
  submitLabel: string;
}) {
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const currentMemberName = ((user as any)?.team_member_name || user?.username || '').trim();
  const { data: membersData } = useQuery({
    queryKey: ['settings', 'team-members'],
    queryFn: settingsApi.teamMembers.list,
  });
  const { data: websitesData } = useQuery({
    queryKey: ['settings', 'websites'],
    queryFn: settingsApi.websites.list,
  });
  const members = membersData?.data || [];
  const websites = websitesData?.data || [];
  const defaultWebsiteId = defaultValues?.website_id || websites[0]?.id || '';
  const currentMember = members.find((m: any) =>
    currentMemberName && m.name.toLowerCase() === currentMemberName.toLowerCase()
  );
  const currentTeam = currentMember?.team || defaultValues?.assigned_team || '';
  const initialAssignees = parseAssigneeList(
    (defaultValues as any)?.assigned_to_list,
    defaultValues?.assigned_to || (isMember ? currentMemberName : '')
  );
  const [selectedAssignees, setSelectedAssignees] = React.useState<string[]>(initialAssignees);
  const [followUpBy, setFollowUpBy] = React.useState<string>(
    ((defaultValues as any)?.follow_up_by as string) || (isMember ? currentMemberName : '')
  );

  const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      priority: 'Medium',
      status: 'Pending',
      website_id: defaultWebsiteId as any,
      estimated_hours: 0,
      estimated_minutes: 0,
      ...defaultValues,
      assigned_to: defaultValues?.assigned_to || (isMember ? currentMemberName : ''),
      assigned_team: defaultValues?.assigned_team || (isMember ? currentTeam : ''),
      assigned_to_list: initialAssignees,
      follow_up_by: ((defaultValues as any)?.follow_up_by as string) || (isMember ? currentMemberName : ''),
    },
  });

  React.useEffect(() => {
    const assignees = parseAssigneeList(
      (defaultValues as any)?.assigned_to_list,
      defaultValues?.assigned_to || (isMember ? currentMemberName : '')
    );
    setSelectedAssignees(assignees);
    setFollowUpBy(((defaultValues as any)?.follow_up_by as string) || (isMember ? currentMemberName : ''));
    reset({
      priority: 'Medium',
      status: 'Pending',
      website_id: defaultWebsiteId as any,
      estimated_hours: 0,
      estimated_minutes: 0,
      ...defaultValues,
      assigned_to: defaultValues?.assigned_to || (isMember ? currentMemberName : ''),
      assigned_team: defaultValues?.assigned_team || (isMember ? currentTeam : ''),
      assigned_to_list: assignees,
      follow_up_by: ((defaultValues as any)?.follow_up_by as string) || (isMember ? currentMemberName : ''),
    });
  }, [currentMemberName, currentTeam, defaultValues, defaultWebsiteId, isMember, reset]);

  const teamOptions = [
    { value: '', label: 'Select team' },
    ...TEAMS.map(t => ({ value: t, label: t })),
  ];
  const websiteOptions = [
    { value: '', label: 'Select website' },
    ...websites.map((site: any) => ({ value: String(site.id), label: site.name })),
  ];

  const handleFormSubmit = (data: TaskFormData) => {
    onSubmit({
      ...data,
      assigned_to: selectedAssignees.join(', ') || undefined,
      assigned_to_list: selectedAssignees,
      assigned_team: getTeamsForAssignees(selectedAssignees, members, data.assigned_team || ''),
      follow_up_by: followUpBy || null,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Select label="Website *" {...register('website_id')} options={websiteOptions} error={errors.website_id?.message as string} />
      <Input label="Title *" {...register('title')} error={errors.title?.message} placeholder="e.g. Fix 404 Pages" />
      <Textarea label="Description" {...register('description')} rows={3} placeholder="Optional description..." />
      <div className="grid grid-cols-2 gap-4">
        <Select label="Team" {...register('assigned_team')} options={teamOptions} />
        <FollowUpOwnerSelect members={members} value={followUpBy} onChange={setFollowUpBy} />
      </div>
      <MultiAssigneePicker
        label="Assigned To"
        members={members}
        value={selectedAssignees}
        onChange={assignees => {
          setSelectedAssignees(assignees);
          const team = getTeamsForAssignees(assignees, members);
          if (team) setValue('assigned_team', team as any);
        }}
      />
      <div className="grid grid-cols-2 gap-4">
        <Select label="Priority" {...register('priority')} options={PRIORITIES.map(p => ({ value: p, label: p }))} />
        <Select label="Status" {...register('status')} options={STATUSES.map(s => ({ value: s, label: s }))} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input label="Due Date *" type="date" {...register('due_date')} error={errors.due_date?.message} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Time Estimate *</label>
          <div className="flex gap-2">
            <select {...register('estimated_hours')}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
              {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
            </select>
            <select {...register('estimated_minutes')}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
              {MINUTES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          {errors.estimated_minutes && (
            <p className="mt-1.5 text-xs text-danger">{errors.estimated_minutes.message}</p>
          )}
        </div>
      </div>
      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <NotesEditor
            value={field.value}
            onChange={field.onChange}
            rows={3}
            placeholder="Any additional notes..."
          />
        )}
      />
      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" loading={loading}>{submitLabel}</Button>
      </div>
    </form>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA]">{label}</p>
      <p className="mt-1 text-sm font-medium text-[#18181B] break-words">{value || '—'}</p>
    </div>
  );
}

// Inline hours/minutes editor — lets a member adjust the time estimate on their own
// task without opening the full edit form (which stays admin-only).
function EstimateEditor({ task, onSave, saving }: { task: any; onSave: (hours: number, minutes: number) => void; saving?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState<number>(task.estimated_hours || 0);
  const [minutes, setMinutes] = useState<number>(task.estimated_minutes || 0);

  useEffect(() => {
    setHours(task.estimated_hours || 0);
    setMinutes(task.estimated_minutes || 0);
  }, [task.estimated_hours, task.estimated_minutes]);

  const estimate = (task.estimated_hours > 0 || task.estimated_minutes > 0)
    ? `${task.estimated_hours > 0 ? task.estimated_hours + 'h ' : ''}${task.estimated_minutes > 0 ? task.estimated_minutes + 'm' : ''}`.trim()
    : null;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input type="number" min={0} max={999} value={hours}
          onChange={e => setHours(Math.max(0, Math.min(999, Number(e.target.value))))}
          className="w-14 border border-[#E4E4E7] rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#F97316]" />
        <span className="text-xs text-[#A1A1AA]">h</span>
        <input type="number" min={0} max={59} value={minutes}
          onChange={e => setMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
          className="w-14 border border-[#E4E4E7] rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#F97316]" />
        <span className="text-xs text-[#A1A1AA]">m</span>
        <button onClick={() => { onSave(hours, minutes); setEditing(false); }} disabled={saving}
          className="text-xs font-semibold text-green-600 hover:underline ml-1">Save</button>
        <button onClick={() => { setEditing(false); setHours(task.estimated_hours || 0); setMinutes(task.estimated_minutes || 0); }}
          className="text-xs text-[#A1A1AA] hover:underline">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span>{estimate || '—'}</span>
      <button onClick={() => setEditing(true)} className="p-0.5 text-[#A1A1AA] hover:text-[#2563EB]" title="Edit time">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
      </button>
    </div>
  );
}

// Read-only task view — members use this instead of opening the edit form just to see details.
// Time estimate stays editable for whoever the task is visible to (their own task).
function TaskDetailView({ task, onSaveEstimate, savingEstimate }: { task: any; onSaveEstimate: (hours: number, minutes: number) => void; savingEstimate?: boolean }) {
  const overdueDays = getOverdueDays(task.due_date, task.status);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={task.status} />
        <PriorityBadge priority={task.priority} />
        {overdueDays && <OverdueBadge days={overdueDays} />}
        {task.assigned_team && <TeamBadge team={task.assigned_team} />}
      </div>

      <h3 className="text-base font-bold text-[#18181B] leading-snug">{task.title}</h3>

      <div className="grid grid-cols-2 gap-3">
        <DetailField label="Website" value={task.website_name || 'Tkxel'} />
        <DetailField label="Assigned To" value={formatAssigneeNames(task)} />
        <DetailField label="Follow-up" value={task.follow_up_by} />
        <DetailField label="Due Date" value={formatDate(task.due_date)} />
        <DetailField label="Time Estimate" value={<EstimateEditor task={task} onSave={onSaveEstimate} saving={savingEstimate} />} />
        <DetailField label="Created By" value={task.created_by} />
      </div>

      {task.description && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Description</p>
          <FormattedNotes value={task.description} />
        </div>
      )}

      {task.notes && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1">Notes</p>
          <FormattedNotes value={task.notes} />
        </div>
      )}
    </div>
  );
}

export default function SitewideTasks() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const isAdmin = (user as any)?.role === 'admin';
  const currentMemberName = ((user as any)?.team_member_name || user?.username || '').trim();
  const currentUsername = (user?.username || '').trim();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editTask, setEditTask] = useState<any>(null);
  const [viewTask, setViewTask] = useState<any>(null);
  const [deleteTask, setDeleteTask] = useState<any>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const { data: websitesData } = useQuery({
    queryKey: ['settings', 'websites'],
    queryFn: settingsApi.websites.list,
  });
  const websites = websitesData?.data || [];

  // Listen for header "Add Task" button
  React.useEffect(() => {
    const handler = () => setAddOpen(true);
    window.addEventListener('header:add-task', handler);
    return () => window.removeEventListener('header:add-task', handler);
  }, []);

  const params: Record<string, string> = {};
  if (filters.team) params.team = filters.team;
  if (filters.status) params.status = filters.status;
  if (filters.priority) params.priority = filters.priority;
  if (filters.website_id) params.website_id = filters.website_id;
  if (search) params.search = search;

  const { data, isLoading } = useQuery({
    queryKey: ['sitewide-tasks', params],
    queryFn: () => sitewideApi.list(params),
  });

  const createMutation = useMutation({
    mutationFn: sitewideApi.create,
    onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
            qc.invalidateQueries({ queryKey: ['dashboard'] });
      setAddOpen(false);
      toast.success('Task created');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => sitewideApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setEditTask(null);
      toast.success('Task updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const estimateMutation = useMutation({
    mutationFn: ({ id, estimated_hours, estimated_minutes }: { id: number; estimated_hours: number; estimated_minutes: number }) =>
      sitewideApi.update(id, { estimated_hours, estimated_minutes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Time estimate updated');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update time estimate'),
  });

  const deleteMutation = useMutation({
    mutationFn: sitewideApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setDeleteTask(null);
      toast.success('Task deleted');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: sitewideApi.bulkDelete,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setSelectedTaskIds([]);
      setBulkDeleteOpen(false);
      toast.success(`Deleted ${data.deleted ?? 'selected'} tasks`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => sitewideApi.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['sitewide-tasks', params] });
      const prev = qc.getQueryData(['sitewide-tasks', params]);
      qc.setQueryData(['sitewide-tasks', params], (old: any) => ({
        ...old,
        data: old.data.map((t: any) => t.id === id ? { ...t, status } : t),
      }));
      return { prev };
    },
    onError: (_err, _vars, context) => {
      qc.setQueryData(['sitewide-tasks', params], context?.prev);
      toast.error('Failed to update status');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['sitewide-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const tasks = (data?.data || []).filter((task: any) =>
    !isMember ||
    memberCanSeeTask(task, currentMemberName, currentUsername)
  );
  const total = isMember ? tasks.length : data?.total || 0;
  const hasFilters = !!(filters.website_id || filters.team || filters.status || filters.priority || search);
  const visibleTaskIds = tasks.map((task: any) => Number(task.id));
  const allVisibleSelected = visibleTaskIds.length > 0 && visibleTaskIds.every((id: number) => selectedTaskIds.includes(id));

  React.useEffect(() => {
    setSelectedTaskIds(selected => selected.filter((id: number) => visibleTaskIds.includes(id)));
  }, [visibleTaskIds.join(',')]);

  const toggleTaskSelection = (id: number) => {
    setSelectedTaskIds(selected =>
      selected.includes(id) ? selected.filter(selectedId => selectedId !== id) : [...selected, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedTaskIds(allVisibleSelected ? [] : visibleTaskIds);
  };

  return (
    <div className="space-y-5 max-w-[1400px]">

      {/* Filter Bar */}
      <div
        className="bg-white rounded-xl px-5 py-4 flex flex-wrap items-center gap-3"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E4E4E7' }}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search tasks..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#18181B] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:border-[#F97316] transition-all duration-150"
            style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
          />
        </div>

        {/* Team filter */}
        <select
          value={filters.website_id || ''}
          onChange={e => setFilters(f => ({ ...f, website_id: e.target.value }))}
          className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:border-[#F97316] transition-all duration-150 min-w-[150px]"
          style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
        >
          <option value="">All Websites</option>
          {websites.map((site: any) => (
            <option key={site.id} value={site.id}>{site.name}</option>
          ))}
        </select>

        {/* Team filter */}
        <select
          value={filters.team || ''}
          onChange={e => setFilters(f => ({ ...f, team: e.target.value }))}
          className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:border-[#F97316] transition-all duration-150 min-w-[130px]"
          style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
        >
          <option value="">All Teams</option>
          {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Status filter */}
        <select
          value={filters.status || ''}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:border-[#F97316] transition-all duration-150 min-w-[140px]"
          style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* Priority filter */}
        <select
          value={filters.priority || ''}
          onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
          className="px-3 py-2 text-sm border border-[#E4E4E7] rounded-lg bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:border-[#F97316] transition-all duration-150 min-w-[140px]"
          style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
        >
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        {hasFilters && (
          <button
            onClick={() => { setFilters({}); setSearch(''); }}
            className="text-sm text-[#DC2626] hover:text-red-800 px-2 py-1 hover:bg-red-50 rounded-lg transition-all duration-150 font-medium"
          >
            Clear filters
          </button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Count + Add button */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#71717A]">{total} task{total !== 1 ? 's' : ''}</span>
          {isAdmin && selectedTaskIds.length > 0 && (
            <button
              type="button"
              onClick={() => setBulkDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-[#DC2626] hover:bg-red-100"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete selected ({selectedTaskIds.length})
            </button>
          )}
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-4 py-2 rounded-lg transition-all duration-150"
            style={{ backgroundColor: '#F97316' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EA6C0A')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F97316')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Task
          </button>
        </div>
      </div>

      {/* Table */}
      <div
        className="bg-white rounded-xl overflow-hidden"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E4E4E7' }}
      >
        {isLoading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E4E4E7' }}>
                  {isAdmin && (
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        className="h-4 w-4 rounded border-[#D4D4D8] text-[#F97316] focus:ring-[#F97316]"
                        aria-label="Select all visible sitewide tasks"
                      />
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Title</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Website</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Team</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Assigned</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Priority</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Due Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Estimate</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-[#71717A] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task: any, i: number) => (
                  <tr
                    key={task.id}
                    className="group border-b border-[#F4F4F5] transition-colors duration-150"
                    style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F4F5')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#FFFFFF' : '#FAFAFA')}
                  >
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.includes(Number(task.id))}
                          onChange={() => toggleTaskSelection(Number(task.id))}
                          className="h-4 w-4 rounded border-[#D4D4D8] text-[#F97316] focus:ring-[#F97316]"
                          aria-label={`Select sitewide task ${task.id}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">{task.id}</td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="font-semibold text-[#18181B] truncate">{task.title}</p>
                      {task.notes && (
                        <FormattedNotes value={task.notes} compact tone="muted" className="mt-0.5 max-w-xs" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded bg-[#F4F4F5] px-2 py-1 text-xs font-semibold text-[#52525B]">
                        {task.website_name || 'Tkxel'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {task.assigned_team ? <TeamBadge team={task.assigned_team} /> : <span className="text-[#D4D4D8]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#52525B] text-sm">
                      {formatAssigneeNames(task) ? (
                        <div className="space-y-0.5">
                          <p>{formatAssigneeNames(task)}</p>
                          {task.follow_up_by && (
                            <p className="text-[11px] text-[#A1A1AA]">Follow-up: {task.follow_up_by}</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <span className="text-[#D4D4D8]">—</span>
                          {task.follow_up_by && (
                            <p className="text-[11px] text-[#A1A1AA]">Follow-up: {task.follow_up_by}</p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><PriorityBadge priority={task.priority} /></td>
                    <td className="px-4 py-3">
                      <select
                        value={task.status}
                        onChange={e => statusMutation.mutate({ id: task.id, status: e.target.value })}
                        className="text-xs border border-[#E4E4E7] rounded-lg px-2 py-1.5 bg-white text-[#18181B] focus:outline-none focus:ring-2 focus:border-[#F97316] cursor-pointer transition-all duration-150"
                        style={{ '--tw-ring-color': 'rgba(249,115,22,0.2)' } as any}
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-[#71717A] text-xs whitespace-nowrap">{formatDate(task.due_date)}</td>
                    <td className="px-4 py-3 text-[#71717A] text-xs whitespace-nowrap">
                      <EstimateEditor task={task}
                        onSave={(hours, minutes) => estimateMutation.mutate({ id: task.id, estimated_hours: hours, estimated_minutes: minutes })}
                        saving={estimateMutation.isPending} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <button
                          onClick={() => setViewTask(task)}
                          className="p-1.5 hover:bg-[#F4F4F5] rounded-lg text-[#A1A1AA] hover:text-[#52525B] transition-all duration-150"
                          title="View details"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => setEditTask(task)}
                            className="p-1.5 hover:bg-blue-50 rounded-lg text-[#A1A1AA] hover:text-[#2563EB] transition-all duration-150"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => setDeleteTask(task)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-[#A1A1AA] hover:text-[#DC2626] transition-all duration-150"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 11 : 10} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#F4F4F5] flex items-center justify-center">
                          <svg className="w-6 h-6 text-[#A1A1AA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#71717A]">No sitewide tasks found</p>
                          <p className="text-xs text-[#A1A1AA] mt-1">
                            {hasFilters ? 'Try adjusting your filters' : 'Add your first task to get started'}
                          </p>
                        </div>
                        {!hasFilters && (
                          <button
                            onClick={() => setAddOpen(true)}
                            className="text-sm font-semibold text-white px-4 py-2 rounded-lg transition-all duration-150"
                            style={{ backgroundColor: '#F97316' }}
                          >
                            Add Task
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Sitewide Task" size="lg">
        <TaskForm
          key={addOpen ? 'add-open' : 'add-closed'}
          onSubmit={data => createMutation.mutate(data)}
          loading={createMutation.isPending}
          submitLabel="Create Task"
        />
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTask} onClose={() => setEditTask(null)} title="Edit Task" size="lg">
        {editTask && (
          <TaskForm
            defaultValues={editTask}
            onSubmit={data => updateMutation.mutate({ id: editTask.id, data })}
            loading={updateMutation.isPending}
            submitLabel="Save Changes"
          />
        )}
      </Modal>

      <Modal open={!!viewTask} onClose={() => setViewTask(null)} title="Task Details" size="lg">
        {viewTask && (
          <TaskDetailView
            task={tasks.find((t: any) => t.id === viewTask.id) || viewTask}
            onSaveEstimate={(hours, minutes) => estimateMutation.mutate({ id: viewTask.id, estimated_hours: hours, estimated_minutes: minutes })}
            savingEstimate={estimateMutation.isPending}
          />
        )}
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTask}
        onClose={() => setDeleteTask(null)}
        onConfirm={() => deleteMutation.mutate(deleteTask?.id)}
        title="Delete Task"
        message={`Are you sure you want to delete "${deleteTask?.title}"? This action cannot be undone.`}
        loading={deleteMutation.isPending}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedTaskIds)}
        title="Delete Selected Tasks"
        message={`Are you sure you want to delete ${selectedTaskIds.length} selected sitewide task${selectedTaskIds.length === 1 ? '' : 's'}? This cannot be undone.`}
        loading={bulkDeleteMutation.isPending}
      />
    </div>
  );
}
